import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Trash2, FileText, Link2, Copy, Cake } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCustomInvoice = ({ adminToken, onSuccess }) => {
  const [items, setItems] = useState([
    { name: 'Custom Cake', price: 0, quantity: 1, description: '' }
  ]);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    delivery_address: '',
    delivery_type: 'pickup',
    delivery_charge: 0,
    discount: 0,
    scheduled_date: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const addItem = () => {
    setItems([...items, { name: '', price: 0, quantity: 1, description: '' }]);
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, i) => sum + (Number(i.price) * Number(i.quantity)), 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    return subtotal + Number(formData.delivery_charge) - Number(formData.discount);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.customer_phone) {
      toast.error('Please enter customer name and phone');
      return;
    }
    
    if (items.some(i => !i.name || !i.price)) {
      toast.error('Please fill all item details');
      return;
    }

    if (calculateTotal() <= 0) {
      toast.error('Total must be greater than 0');
      return;
    }

    setLoading(true);
    try {
      const invoiceData = {
        customer_name: formData.customer_name,
        customer_phone: formData.customer_phone,
        delivery_address: formData.delivery_address,
        delivery_type: formData.delivery_type,
        delivery_charge: Number(formData.delivery_charge),
        discount: Number(formData.discount),
        scheduled_date: formData.scheduled_date,
        notes: formData.notes,
        items: items.map(i => ({
          name: i.name,
          price: Number(i.price),
          quantity: Number(i.quantity),
          description: i.description
        }))
      };

      const response = await axios.post(`${API}/admin/custom-invoice`, invoiceData, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });

      setResult(response.data);
      toast.success('Custom invoice created!');
      onSuccess?.();
    } catch (error) {
      toast.error('Failed to create invoice');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const copyPaymentLink = () => {
    if (result?.payment_link) {
      navigator.clipboard.writeText(result.payment_link);
      toast.success('Payment link copied!');
    }
  };

  const resetForm = () => {
    setItems([{ name: 'Custom Cake', price: 0, quantity: 1, description: '' }]);
    setFormData({
      customer_name: '',
      customer_phone: '',
      delivery_address: '',
      delivery_type: 'pickup',
      delivery_charge: 0,
      discount: 0,
      scheduled_date: '',
      notes: ''
    });
    setResult(null);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Items */}
      <Card className="p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-accent flex items-center gap-2">
            <Cake className="h-5 w-5" />
            Invoice Items
          </h3>
          <Button type="button" variant="outline" size="sm" onClick={addItem}>
            <Plus className="h-4 w-4 mr-1" />
            Add Item
          </Button>
        </div>

        <div className="space-y-4">
          {items.map((item, index) => (
            <div key={index} className="p-3 bg-gray-50 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm text-muted-foreground">Item {index + 1}</span>
                {items.length > 1 && (
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 text-red-500"
                    onClick={() => removeItem(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs">Item Name *</Label>
                  <Input
                    value={item.name}
                    onChange={(e) => updateItem(index, 'name', e.target.value)}
                    placeholder="e.g., Custom Birthday Cake"
                  />
                </div>
                <div>
                  <Label className="text-xs">Price (₹) *</Label>
                  <Input
                    type="number"
                    value={item.price}
                    onChange={(e) => updateItem(index, 'price', e.target.value)}
                    placeholder="0"
                    min="0"
                  />
                </div>
                <div>
                  <Label className="text-xs">Quantity</Label>
                  <Input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                    placeholder="1"
                    min="1"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Description</Label>
                  <Input
                    value={item.description}
                    onChange={(e) => updateItem(index, 'description', e.target.value)}
                    placeholder="e.g., 2kg chocolate truffle with photo print"
                  />
                </div>
              </div>
              
              <div className="text-right text-sm font-medium text-primary">
                Subtotal: ₹{Number(item.price) * Number(item.quantity)}
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="mt-4 p-4 bg-amber-50 rounded-lg space-y-2">
          <div className="flex justify-between text-sm">
            <span>Items Subtotal:</span>
            <span>₹{calculateSubtotal()}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Delivery Charge:</span>
            <span>₹{formData.delivery_charge}</span>
          </div>
          {formData.discount > 0 && (
            <div className="flex justify-between text-sm text-green-600">
              <span>Discount:</span>
              <span>-₹{formData.discount}</span>
            </div>
          )}
          <div className="flex justify-between font-bold text-lg pt-2 border-t">
            <span>Total:</span>
            <span className="text-primary">₹{calculateTotal()}</span>
          </div>
        </div>
      </Card>

      {/* Customer Details */}
      <Card className="p-4 sm:p-6">
        <h3 className="text-lg font-bold text-accent mb-4">Customer Details</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Customer Name *</Label>
              <Input
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                placeholder="Customer name"
              />
            </div>
            <div>
              <Label>Phone *</Label>
              <Input
                value={formData.customer_phone}
                onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                placeholder="+91..."
              />
            </div>
          </div>

          <div>
            <Label>Delivery Type</Label>
            <Select
              value={formData.delivery_type}
              onValueChange={(value) => setFormData({ ...formData, delivery_type: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="delivery">Home Delivery</SelectItem>
                <SelectItem value="pickup">Store Pickup</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.delivery_type === 'delivery' && (
            <div>
              <Label>Delivery Address</Label>
              <Textarea
                value={formData.delivery_address}
                onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
                placeholder="Full delivery address"
                rows={2}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Delivery Charge (₹)</Label>
              <Input
                type="number"
                value={formData.delivery_charge}
                onChange={(e) => setFormData({ ...formData, delivery_charge: e.target.value })}
                placeholder="0"
                min="0"
              />
            </div>
            <div>
              <Label>Discount (₹)</Label>
              <Input
                type="number"
                value={formData.discount}
                onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                placeholder="0"
                min="0"
              />
            </div>
          </div>

          <div>
            <Label>Scheduled Date (Optional)</Label>
            <Input
              type="date"
              value={formData.scheduled_date}
              onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
            />
          </div>

          <div>
            <Label>Notes (Optional)</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Special instructions, design details..."
              rows={2}
            />
          </div>

          <Button
            type="submit"
            className="w-full h-12"
            disabled={loading}
          >
            {loading ? 'Creating...' : (
              <>
                <FileText className="h-4 w-4 mr-2" />
                Create Invoice & Payment Link
              </>
            )}
          </Button>
        </form>

        {/* Result */}
        {result && (
          <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="font-bold text-green-800">Invoice Created!</h4>
                <p className="text-sm text-green-700">Order ID: {result.order_id?.slice(0, 8).toUpperCase()}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-green-800">₹{result.total}</p>
              </div>
            </div>
            
            {result.payment_link && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-green-700">Payment Link:</p>
                <div className="flex items-center gap-2">
                  <Input
                    value={result.payment_link}
                    readOnly
                    className="flex-1 text-sm bg-white"
                  />
                  <Button size="icon" variant="outline" onClick={copyPaymentLink}>
                    <Copy className="h-4 w-4" />
                  </Button>
                  <a href={result.payment_link} target="_blank" rel="noopener noreferrer">
                    <Button size="icon" variant="outline">
                      <Link2 className="h-4 w-4" />
                    </Button>
                  </a>
                </div>
              </div>
            )}
            
            <Button variant="outline" className="w-full mt-3" onClick={resetForm}>
              Create Another Invoice
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default AdminCustomInvoice;
